/**
 * RegistrationResolutionWorkspace
 *
 * Full-canvas triage page for DRAFT registrations. Pixel-faithful port
 * of the team-approved mockup at
 * ``~/.claude/plans/amanda-draft-resolution-mockup.html``.
 *
 * Structure:
 *
 *   ─── Editorial header
 *       eyebrow + status pills  |  created/channel + actions
 *       serif title (address)
 *       subtitle (product)
 *       meta line (uuid · date · org · contractor)
 *
 *   ─── 4 smart-signal tiles
 *       Contractor tier · Install window · Payment · Exceptions required
 *
 *   ─── Rule evaluation card (all 7 canonical slots)
 *       Passing rules render with a green check; failing slots render
 *       with the live WorkflowIssue's summary + detail + a status chip.
 *
 *   ─── Apply exceptions card
 *       De-duplicated shelf of every exception surfaced by any open
 *       issue. Each row lists which rule(s) it covers and is
 *       pre-ticked when the matching issue has a default reason.
 *
 *   ─── Justification textarea
 *   ─── 3 context cards (Homeowner · Property · Contractor)
 *   ─── Claim history band
 *   ─── Super Admin footer stubs
 *   ─── Dark sticky action slab (Deny · Activate)
 *
 * All live data comes from ``useHolding`` + ``useWorkflowIssues`` +
 * ``useResolveWorkflowIssue``. Canonical rule slots and the
 * issue→slot matcher are seed-adjacent copy - follow-up to move into
 * ``policy_snapshot`` so the engine stays generic.
 */

import { useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  CreditCard,
  FileText,
  MapPin,
  Pencil,
  Plus,
  ShieldCheck,
  Sparkles,
  Users,
  UserCircle,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/shared/components/ui/button";
import { useTranslation } from "react-i18next";

import { useHolding } from "@/admin/hooks/use-products";
import {
  useResolveWorkflowIssue,
  useWorkflowIssues,
} from "@/shared/hooks/use-workflows";
import type {
  WorkflowIssue,
  WorkflowIssueResolvePayload,
} from "@/shared/lib/workflows/types";
import { toastApiError } from "@/shared/lib/api-error";
import { cn } from "@/shared/lib/utils";
import { useModal } from "@/shared/components/modals/modal-provider";
import { addressDisplay, useFormatters } from "@/shared/lib/formatting";
import { formatPhoneDisplay } from "@/shared/lib/forms";

import { WorkflowIssueResolveModal } from "./workflow-issue-resolve-modal";

interface RegistrationResolutionWorkspaceProps {
  holdingUuid: string;
  canApprove: boolean;
  /**
   * Name of the product being registered - used in subtitle copy.
   */
  warrantyIntentLabel?: string;
  /**
   * Name of the extended / upgraded warranty tier under review
   * (Platinum, Preferred, Extended, …). Standard tier is always on
   * and is not what this workspace decides; Amanda is deciding
   * whether to grant the upgrade.
   */
  upgradeTierLabel?: string;
  claims?: Array<{
    uuid: string;
    raisedOn: string;
    summary: string;
    amount?: string;
    statusLabel: string;
    statusVariant: "open" | "paid" | "closed";
  }>;
  onClose?: () => void;
  /**
   * When set, renders an editorial "← BACK TO …" link at the top-left
   * instead of the dialog-style X. Use this when the workspace is
   * mounted as a full page so the user sees a familiar return path.
   */
  backLabel?: string;
}

type ClaimRow = NonNullable<
  RegistrationResolutionWorkspaceProps["claims"]
>[number];

// ---------------------------------------------------------------------------
// Rule evaluation is entirely backend-driven. Each open
// :class:`WorkflowIssue` is a failing rule; the engine does not
// currently expose the list of *passing* rules, so we simply render
// what the backend tells us and surface a count of blockers. When
// Tolu adds a ``policy_snapshot`` enumerating all evaluated rules,
// the "passed" side can be populated from that payload without a
// renderer change.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Confetti
// ---------------------------------------------------------------------------

function launchConfetti(host: HTMLElement | null) {
  if (!host) return;
  host.innerHTML = "";
  const colors = [
    "#1c5a48",
    "#a8bfab",
    "#c5a94c",
    "#4a4a46",
    "#9a9a94",
    "#d9cfa8",
  ];
  for (let i = 0; i < 80; i += 1) {
    const p = document.createElement("span");
    p.style.position = "absolute";
    p.style.top = "-20px";
    p.style.left = `${Math.random() * 100}%`;
    p.style.width = "8px";
    p.style.height = "12px";
    p.style.borderRadius = "2px";
    p.style.background = colors[Math.floor(Math.random() * colors.length)];
    p.style.transform = `rotate(${Math.random() * 360}deg)`;
    p.style.animation = `confetti-fall ${1.8 + Math.random() * 1.6}s ease-out ${Math.random() * 0.4}s forwards`;
    host.appendChild(p);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function daysBetween(fromIso: string | null | undefined): number | null {
  if (!fromIso) return null;
  const from = new Date(fromIso);
  if (Number.isNaN(from.getTime())) return null;
  const diffMs = Date.now() - from.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function formatDate(
  iso: string | null | undefined,
  locale: string,
  opts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  },
): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(locale, opts);
}

type HoldingShape = {
  uuid?: string;
  status?: string;
  created_at?: string;
  consumer_account?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
  };
  host?: {
    name?: string;
    address?: import("@/shared/lib/formatting").Address | null;
    host_template?: { name?: string };
    kind?: string;
  };
  partnership?: {
    downstream_organisation_name?: string;
    attribute_values?: Array<{ code_name: string; value: unknown }>;
  };
  product?: {
    cached_short_name?: string;
    cached_long_name?: string;
  };
  organisation?: { name?: string };
  warranties?: Array<{
    active_from?: string | null;
    // The tenant's own category name ('Standard' for OC) and the platform
    // warranty type. Both already come back from ``useHolding``; this
    // interface simply had not asked for them.
    class_name?: string;
    warranty_type?: string;
  }>;
  workflow_states?: Array<{
    workflow_key: string;
    state_key: string;
    state_label: string;
  }>;
};

// Title fallback for a registration with no resolvable host/address/product
// name. The lifecycle "draft" state carries a tenant-configured label (OC calls
// it "Inactive"; WPS-82), so resolve that label rather than hardcoding "Draft".
function draftFallbackTitle(holding: HoldingShape | undefined): string {
  const statusState = holding?.workflow_states?.find(
    (s) => s.workflow_key === "lifecycle_status",
  );
  const label =
    statusState?.state_key === "draft" ? statusState.state_label : undefined;
  return `${label || "Draft"} registration`;
}

function buildAddress(holding: HoldingShape | undefined): string {
  const h = holding?.host;
  if (!h)
    return holding?.product?.cached_short_name ?? draftFallbackTitle(holding);
  return (
    addressDisplay(h.address) ||
    h.name ||
    holding?.product?.cached_short_name ||
    draftFallbackTitle(holding)
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RegistrationResolutionWorkspace({
  holdingUuid,
  canApprove,
  warrantyIntentLabel,
  upgradeTierLabel = "extended warranty",
  claims = [],
  onClose,
  backLabel,
}: RegistrationResolutionWorkspaceProps) {
  const { t } = useTranslation();
  const { data: holdingData } = useHolding(holdingUuid);
  const holding = holdingData as HoldingShape | undefined;
  // The cover this registration already holds, named by the tenant rather than
  // by us. Every sentence below used to say "Standard", which is OC's name for
  // it - 12 Assured issues no such tier at all, so their staff were told about
  // cover they do not sell.
  const currentCoverLabel =
    holding?.warranties?.[0]?.class_name ||
    t("registrations.baseCover", "Base");
  const { openModal } = useModal();
  const { locale } = useFormatters();

  const listParams = useMemo(
    () => ({
      resource_type: "holding",
      resource_id: holdingUuid,
      status: "OPEN" as const,
      page_size: 50,
    }),
    [holdingUuid],
  );
  const { data: issuesList } = useWorkflowIssues(listParams);
  const resolveMutation = useResolveWorkflowIssue();

  const openIssues: WorkflowIssue[] = useMemo(
    () =>
      Array.isArray(issuesList)
        ? issuesList
        : ((issuesList as { results?: WorkflowIssue[] } | undefined)?.results ??
          []),
    [issuesList],
  );

  const outstanding = openIssues.length;
  const allClear = outstanding === 0;

  // ── Exception picker state ───────────────────────────────────────
  // De-dupe reason_options across all open issues by code; keep a
  // count of how many issues each exception covers so the shelf row
  // can show "· Covers N rules".
  const exceptionShelf = useMemo(() => {
    type Shelf = {
      code: string;
      label: string;
      issues: WorkflowIssue[];
    };
    const byCode = new Map<string, Shelf>();
    for (const issue of openIssues) {
      for (const opt of issue.reason_options) {
        const existing = byCode.get(opt.code);
        if (existing) {
          existing.issues.push(issue);
        } else {
          byCode.set(opt.code, {
            code: opt.code,
            label: opt.label,
            issues: [issue],
          });
        }
      }
    }
    return Array.from(byCode.values());
  }, [openIssues]);

  // ``openIssues`` is empty on the very first render (react-query
  // hasn't resolved yet), so we derive the default ticks from the
  // resolved issue list via ``useMemo``. User overrides (toggles,
  // custom-exception creates) live in a separate state slot and, once
  // set, win over the derived default. This avoids the set-state-in-
  // effect anti-pattern while still pre-ticking the obvious reason.
  const defaultSeedCodes = useMemo(() => {
    const s = new Set<string>();
    for (const issue of openIssues) {
      if (issue.reason_options.length > 0) s.add(issue.reason_options[0].code);
    }
    return s;
  }, [openIssues]);
  const [userSelections, setUserSelections] = useState<Set<string> | null>(
    null,
  );
  const selectedCodes = userSelections ?? defaultSeedCodes;
  const setSelectedCodes = (updater: (prev: Set<string>) => Set<string>) => {
    setUserSelections((prev) => updater(prev ?? new Set(defaultSeedCodes)));
  };

  const toggleException = (code: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const [justification, setJustification] = useState("");
  const [showJustificationError, setShowJustificationError] = useState(false);
  const justificationRef = useRef<HTMLTextAreaElement | null>(null);
  const confettiHost = useRef<HTMLDivElement | null>(null);

  // ── Local editable catalog state ─────────────────────────────────
  // Amanda can rename rules / exceptions she sees and also add her
  // own. The edits live in component state for the demo; persisting
  // them to the seeded ``Rule`` / ``ExceptionReason`` catalog needs a
  // Tolu-owned backend endpoint (``PATCH /workflows/rules/<uuid>/``
  // and ``POST /workflows/exception-reasons/``). Flagged follow-up.
  const [ruleOverrides, setRuleOverrides] = useState<
    Record<string, { summary: string; detail: string }>
  >({});
  const [excOverrides, setExcOverrides] = useState<
    Record<string, { label: string }>
  >({});
  const [customRules, setCustomRules] = useState<
    Array<{ id: string; summary: string; detail: string }>
  >([]);
  const [customExceptions, setCustomExceptions] = useState<
    Array<{ code: string; label: string }>
  >([]);
  const [editingRuleUuid, setEditingRuleUuid] = useState<string | null>(null);
  const [editingExcCode, setEditingExcCode] = useState<string | null>(null);
  const [addingRule, setAddingRule] = useState(false);
  const [addingExc, setAddingExc] = useState(false);

  const saveRuleEdit = (uuid: string, summary: string, detail: string) => {
    setRuleOverrides((prev) => ({ ...prev, [uuid]: { summary, detail } }));
    setEditingRuleUuid(null);
    toast.success("Rule updated (demo - not yet synced to catalog)");
  };
  const addCustomRule = (summary: string, detail: string) => {
    setCustomRules((prev) => [
      ...prev,
      { id: `custom-${Date.now()}`, summary, detail },
    ]);
    setAddingRule(false);
    toast.success("Rule added (demo - not yet synced to catalog)");
  };
  const saveExcEdit = (code: string, label: string) => {
    setExcOverrides((prev) => ({ ...prev, [code]: { label } }));
    setEditingExcCode(null);
    toast.success("Exception updated (demo - not yet synced to catalog)");
  };
  const addCustomException = (label: string) => {
    const code = `custom-${Date.now()}`;
    setCustomExceptions((prev) => [...prev, { code, label }]);
    setSelectedCodes((prev) => new Set(prev).add(code));
    setAddingExc(false);
    toast.success("Exception added (demo - not yet synced to catalog)");
  };

  // ── Signals ─────────────────────────────────────────────────────
  const contractorName =
    holding?.partnership?.downstream_organisation_name ?? null;
  const tierAttr = holding?.partnership?.attribute_values?.find((a) =>
    /tier|membership/i.test(a.code_name),
  );
  const tierRaw = String(tierAttr?.value ?? "").toLowerCase();
  // The dealer's OWN membership tier, as they recorded it. This used to map
  // the raw value onto one of four hardcoded labels and fall through to
  // 'Standard' - so a tenant whose tiers are Bronze/Silver/Gold saw every
  // dealer labelled "Standard", and a dealer with no tier recorded looked
  // like a real tier rather than a gap.
  const tierLabel = tierRaw
    ? String(tierAttr?.value ?? "")
    : t("registrations.contractorTierUnset", "Not recorded");
  // Tone stays a substring match on purpose: it is decoration, and an
  // unrecognised tier degrades to neutral rather than being mislabelled.
  const tierTone = tierRaw.includes("platinum")
    ? "platinum"
    : tierRaw.includes("preferred") || tierRaw.includes("gold")
      ? "success"
      : "neutral";

  const installFrom =
    holding?.warranties?.[0]?.active_from ?? holding?.created_at ?? null;
  const daysSince = daysBetween(installFrom);
  const installValue =
    daysSince === null
      ? "Not tracked"
      : daysSince <= 90
        ? "Within 90 days"
        : "Past 90 days";
  const installTone: TileTone =
    daysSince === null ? "neutral" : daysSince <= 90 ? "success" : "err";
  const installMeta =
    daysSince === null
      ? undefined
      : `${daysSince} day${daysSince === 1 ? "" : "s"} since install · ${formatDate(installFrom, locale)}`;

  const paymentState = holding?.workflow_states?.find((s) =>
    /payment/i.test(s.workflow_key),
  );
  const paymentLabel = paymentState?.state_label ?? "Not tracked";
  const paymentTone: TileTone =
    paymentState?.state_key === "paid" || paymentState?.state_key === "prepaid"
      ? "success"
      : paymentState?.state_key === "unpaid"
        ? "err"
        : "neutral";

  const exceptionsLabel = allClear
    ? "All clear"
    : `${outstanding} blocker${outstanding === 1 ? "" : "s"}`;
  const exceptionsTone: TileTone = allClear
    ? "success"
    : outstanding >= 3
      ? "err"
      : "warn";
  const exceptionsMeta = allClear
    ? "Ready to grant upgrade"
    : `${outstanding} rule${outstanding === 1 ? "" : "s"} need${outstanding === 1 ? "s" : ""} your call`;

  // ── Activate ────────────────────────────────────────────────────
  async function activate() {
    if (!canApprove) {
      toast.error("You do not have permission to activate this registration");
      return;
    }
    if (openIssues.length === 0) {
      toast.info("Nothing to resolve - this registration is already clear.");
      onClose?.();
      return;
    }

    // For each open issue, pick the subset of its reason_options that
    // the reviewer ticked in the shelf. At least one tick per issue is
    // required - we surface a clear error if any issue has no
    // selection so Amanda isn't left guessing.
    const perIssue = openIssues.map((issue) => {
      const codes = issue.reason_options
        .map((r) => r.code)
        .filter((c) => selectedCodes.has(c));
      return { issue, codes };
    });
    const issueWithoutReason = perIssue.find((p) => p.codes.length === 0);
    if (issueWithoutReason) {
      toast.error("Pick at least one exception that covers every open rule");
      return;
    }

    const sharedText = justification.trim();
    if (!sharedText) {
      setShowJustificationError(true);
      justificationRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      justificationRef.current?.focus();
      return;
    }
    try {
      for (const { issue, codes } of perIssue) {
        const payload: WorkflowIssueResolvePayload = {
          resolution_kind: "exception_granted",
          resolution_text: sharedText,
          reason_codes: codes,
          captured_values: {},
        };
        await resolveMutation.mutateAsync({ uuid: issue.uuid, payload });
      }
      toast.success("Registration activated");
      launchConfetti(confettiHost.current);
      setTimeout(() => onClose?.(), 1800);
    } catch (err) {
      toastApiError(err, "Failed to activate registration");
    }
  }

  // ── Render ──────────────────────────────────────────────────────
  const titleLine = buildAddress(holding);
  const subtitleLine =
    holding?.product?.cached_short_name ?? warrantyIntentLabel;
  const createdLabel = formatDate(holding?.created_at, locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const daysInQueue = daysBetween(holding?.created_at);
  const orgLabel = holding?.organisation?.name ?? "Owens Corning";
  const shortUuid = (holding?.uuid ?? holdingUuid).slice(0, 8);

  return (
    <div
      className="relative flex min-h-full flex-col bg-[var(--bg)]"
      data-testid="resolution-workspace"
    >
      <div
        ref={confettiHost}
        className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
        aria-hidden="true"
      />

      {/* ─── Return affordance - editorial Back link when mounted as
             a full page, dialog-style X when mounted as a modal. */}
      {onClose && !backLabel ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-8 top-8 z-10 flex h-9 w-9 items-center justify-center rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] text-[var(--ink-3)] transition-colors hover:border-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}

      <div className="mx-auto w-full max-w-[1180px] flex-1">
        {onClose && backLabel ? (
          <button
            type="button"
            onClick={onClose}
            className="mx-10 mt-8 inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)] transition-colors hover:text-[var(--ink)]"
          >
            <ArrowLeft className="h-3 w-3" />
            {backLabel}
          </button>
        ) : null}

        {/* ─── Editorial header ────────────────────────────────────── */}
        <header
          className={cn(
            "flex flex-col gap-6 border-b-[0.5px] border-[var(--rule)] px-10 pb-10 sm:flex-row sm:items-end sm:justify-between",
            backLabel ? "pt-5" : "pt-12",
          )}
        >
          <div className="min-w-0 flex-1 pr-10">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <span className="clara-label">{upgradeTierLabel} review</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border-[0.5px] border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] px-3 py-1 text-[11.5px] font-medium text-[var(--status-ok)]">
                <ShieldCheck className="h-3 w-3" strokeWidth={2.25} />
                {t(
                  "registrations.coverActiveBadge",
                  "{{cover}} warranty active",
                  {
                    cover: currentCoverLabel,
                  },
                )}
              </span>
              {outstanding > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border-[0.5px] border-[#e0c98a] bg-[var(--warn-soft,#f5ecd3)] px-3 py-1 text-[11.5px] font-medium text-[var(--status-warn)]">
                  <AlertTriangle className="h-3 w-3" strokeWidth={2.25} />
                  Needs your judgement
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border-[0.5px] border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] px-3 py-1 text-[11.5px] font-medium text-[var(--status-ok)]">
                  <Check className="h-3 w-3" strokeWidth={2.5} />
                  Ready to grant
                </span>
              )}
            </div>
            <h1 className="m-0 text-[38px] leading-[1.05] tracking-[-0.01em] text-[var(--ink)] sm:text-[52px] [font-family:'Cormorant_Garamond',Georgia,serif]">
              {titleLine}
            </h1>
            {subtitleLine ? (
              <p className="mt-3 text-[15px] text-[var(--ink-2)]">
                {subtitleLine}
              </p>
            ) : null}
            <p className="mt-2 text-[12.5px] leading-relaxed text-[var(--ink-3)] [font-family:'Cormorant_Garamond',Georgia,serif]">
              {outstanding > 0
                ? t(
                    "registrations.coverActiveDecideUpgrade",
                    "{{cover}} warranty is already active on this registration. You're deciding whether to grant the upgrade to {{tier}}.",
                    { cover: currentCoverLabel, tier: upgradeTierLabel },
                  )
                : t(
                    "registrations.coverActiveGrantTier",
                    "{{cover}} warranty is already active. Grant {{tier}} to apply the extended tier.",
                    { cover: currentCoverLabel, tier: upgradeTierLabel },
                  )}
            </p>
            <p className="mt-2 text-[12.5px] text-[var(--ink-3)]">
              <span className="font-mono tracking-[0.02em]">{shortUuid}</span>
              <Bull />
              Registered {createdLabel}
              <Bull />
              {orgLabel}
              {contractorName ? (
                <>
                  <Bull />
                  Submitted by {contractorName}
                </>
              ) : null}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-1.5 sm:items-end">
            <p className="text-[11.5px] text-[var(--ink-3)]">
              {daysInQueue === null
                ? "Just submitted"
                : `${daysInQueue} day${daysInQueue === 1 ? "" : "s"} in queue · since ${createdLabel}`}
            </p>
            <p className="text-[11.5px] text-[var(--ink-3)]">
              Channel:{" "}
              {paymentState?.workflow_key ? "Contractor portal" : "Admin UI"}
            </p>
            <Button variant="outline" size="sm" className="mt-2.5">
              Request info
            </Button>
          </div>
        </header>

        <div className="flex flex-col gap-10 px-10 py-10">
          {/* ─── Smart-signal tiles ───────────────────────────────── */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SignalTile
              label="Contractor Tier"
              icon={Wrench}
              value={tierLabel}
              tone={tierTone}
              meta={contractorName ?? "Unknown contractor"}
            />
            <SignalTile
              label="Install Window"
              icon={CalendarClock}
              value={installValue}
              tone={installTone}
              meta={installMeta}
            />
            <SignalTile
              label="Payment"
              icon={CreditCard}
              value={paymentLabel}
              tone={paymentTone}
            />
            <SignalTile
              label={`${upgradeTierLabel} blockers`}
              icon={Sparkles}
              value={exceptionsLabel}
              tone={exceptionsTone}
              meta={exceptionsMeta}
            />
          </div>

          {/* ─── Rule evaluation - backend-driven + locally editable ── */}
          <section>
            <SectionHead
              icon={CheckCircle2}
              title={
                allClear && customRules.length === 0
                  ? "Rule evaluation"
                  : `Rule evaluation · ${outstanding + customRules.length} blocker${outstanding + customRules.length === 1 ? "" : "s"}`
              }
              right={
                canApprove ? (
                  <button
                    type="button"
                    onClick={() => setAddingRule(true)}
                    className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--green)] transition-opacity hover:opacity-70"
                  >
                    <Plus className="h-3 w-3" strokeWidth={2.25} />
                    New rule
                  </button>
                ) : null
              }
            />
            <div className="overflow-hidden rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
              {allClear && customRules.length === 0 && !addingRule ? (
                <div className="flex items-center gap-4 px-7 py-6">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--green-bg,#dde7df)] text-[var(--green)]">
                    <Check className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                  <p className="text-[14px] text-[var(--ink)]">
                    No rules are blocking the upgrade. Ready to grant{" "}
                    {upgradeTierLabel}.
                  </p>
                </div>
              ) : (
                <ul>
                  {openIssues.map((issue, idx) => {
                    const override = ruleOverrides[issue.uuid];
                    return (
                      <IssueRow
                        key={issue.uuid}
                        issue={issue}
                        first={idx === 0}
                        summary={override?.summary ?? issue.summary}
                        detail={override?.detail ?? issue.detail}
                        canEdit={canApprove}
                        isEditing={editingRuleUuid === issue.uuid}
                        onStartEdit={() => setEditingRuleUuid(issue.uuid)}
                        onCancelEdit={() => setEditingRuleUuid(null)}
                        onSave={(s, d) => saveRuleEdit(issue.uuid, s, d)}
                        onResolve={() =>
                          openModal(WorkflowIssueResolveModal, {
                            uuid: issue.uuid,
                          })
                        }
                      />
                    );
                  })}
                  {customRules.map((rule, idx) => (
                    <CustomRuleRow
                      key={rule.id}
                      rule={rule}
                      first={openIssues.length === 0 && idx === 0}
                      canEdit={canApprove}
                      isEditing={editingRuleUuid === rule.id}
                      onStartEdit={() => setEditingRuleUuid(rule.id)}
                      onCancelEdit={() => setEditingRuleUuid(null)}
                      onSave={(s, d) => {
                        setCustomRules((prev) =>
                          prev.map((r) =>
                            r.id === rule.id
                              ? { ...r, summary: s, detail: d }
                              : r,
                          ),
                        );
                        setEditingRuleUuid(null);
                        toast.success("Rule updated");
                      }}
                      onDelete={() => {
                        setCustomRules((prev) =>
                          prev.filter((r) => r.id !== rule.id),
                        );
                        toast.success("Rule removed");
                      }}
                      onResolve={() => {
                        setCustomRules((prev) =>
                          prev.filter((r) => r.id !== rule.id),
                        );
                        toast.success("Rule resolved");
                      }}
                    />
                  ))}
                  {addingRule ? (
                    <AddRuleForm
                      onCancel={() => setAddingRule(false)}
                      onSave={addCustomRule}
                      first={
                        openIssues.length === 0 && customRules.length === 0
                      }
                    />
                  ) : null}
                </ul>
              )}
            </div>
          </section>

          {/* ─── Apply exceptions ──────────────────────────────────── */}
          {exceptionShelf.length > 0 || customExceptions.length > 0 ? (
            <section>
              <SectionHead
                icon={Sparkles}
                title={`Apply exceptions to grant ${upgradeTierLabel}`}
                right={
                  canApprove ? (
                    <button
                      type="button"
                      onClick={() => setAddingExc(true)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--green)] transition-opacity hover:opacity-70"
                    >
                      <Plus className="h-3 w-3" strokeWidth={2.25} />
                      New exception
                    </button>
                  ) : (
                    <span className="text-[11px] text-[var(--ink-3)] [font-family:'Cormorant_Garamond',Georgia,serif]">
                      tick every exception that covers an open rule
                    </span>
                  )
                }
              />
              <div className="overflow-hidden rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-8 py-7">
                <p className="mb-5 text-[13.5px] leading-relaxed text-[var(--ink-2)]">
                  The exceptions below are pre-loaded by the rules engine for
                  this registration. Tick the ones that apply - each covers one
                  or more of the open rules - then add a justification and grant
                  the upgrade.{" "}
                  {t(
                    "registrations.noExceptionKeepOnCover",
                    "If no exception covers the gap, keep the registration on {{cover}} instead.",
                    { cover: currentCoverLabel },
                  )}
                </p>
                <div className="flex flex-col">
                  {exceptionShelf.map((exc) => {
                    const override = excOverrides[exc.code];
                    const label = override?.label ?? exc.label;
                    const isEditing = editingExcCode === exc.code;
                    return (
                      <ExceptionRow
                        key={exc.code}
                        code={exc.code}
                        label={label}
                        coverageCount={exc.issues.length}
                        seededNote="Seeded by the rules engine for this registration."
                        selected={selectedCodes.has(exc.code)}
                        canEdit={canApprove}
                        isEditing={isEditing}
                        onToggle={() => canApprove && toggleException(exc.code)}
                        onStartEdit={() => setEditingExcCode(exc.code)}
                        onCancelEdit={() => setEditingExcCode(null)}
                        onSave={(v) => saveExcEdit(exc.code, v)}
                      />
                    );
                  })}
                  {customExceptions.map((exc) => {
                    const isEditing = editingExcCode === exc.code;
                    return (
                      <ExceptionRow
                        key={exc.code}
                        code={exc.code}
                        label={exc.label}
                        coverageCount={null}
                        seededNote="Added by you · applies only on activation; persist via Manage → Exceptions."
                        selected={selectedCodes.has(exc.code)}
                        canEdit={canApprove}
                        isEditing={isEditing}
                        onToggle={() => canApprove && toggleException(exc.code)}
                        onStartEdit={() => setEditingExcCode(exc.code)}
                        onCancelEdit={() => setEditingExcCode(null)}
                        onSave={(v) => {
                          setCustomExceptions((prev) =>
                            prev.map((r) =>
                              r.code === exc.code ? { ...r, label: v } : r,
                            ),
                          );
                          setEditingExcCode(null);
                          toast.success("Exception updated");
                        }}
                        onDelete={() => {
                          setCustomExceptions((prev) =>
                            prev.filter((r) => r.code !== exc.code),
                          );
                          setSelectedCodes((prev) => {
                            const n = new Set(prev);
                            n.delete(exc.code);
                            return n;
                          });
                          toast.success("Exception removed");
                        }}
                      />
                    );
                  })}
                  {addingExc ? (
                    <AddExceptionForm
                      onCancel={() => setAddingExc(false)}
                      onSave={addCustomException}
                    />
                  ) : null}
                </div>

                <div
                  className={cn(
                    "mt-5 rounded-[8px] border-[0.5px] p-4",
                    showJustificationError
                      ? "border-[var(--status-error)] bg-[rgba(153,27,27,0.04)]"
                      : "border-[var(--rule)] bg-[var(--bg-subtle)]",
                  )}
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    <p className="clara-label">
                      Justification · captured in audit log
                    </p>
                    <span className="rounded-[4px] bg-[var(--ink)] px-1.5 py-px text-[10px] font-medium uppercase tracking-[0.08em] text-white">
                      Required
                    </span>
                  </div>
                  <textarea
                    ref={justificationRef}
                    value={justification}
                    onChange={(e) => {
                      setJustification(e.target.value);
                      if (showJustificationError && e.target.value.trim())
                        setShowJustificationError(false);
                    }}
                    placeholder="What makes this override the right call? One or two sentences for your future self."
                    rows={3}
                    className={cn(
                      "w-full rounded-[6px] border-[0.5px] bg-[var(--bg)] px-3.5 py-2.5 text-[13px] leading-relaxed focus:outline-none",
                      showJustificationError
                        ? "border-[var(--status-error)] focus:border-[var(--status-error)]"
                        : "border-[var(--rule-strong)] focus:border-[var(--ink-3)]",
                    )}
                    maxLength={4000}
                  />
                  {showJustificationError ? (
                    <p className="mt-1.5 text-[12px] text-[var(--status-error)]">
                      A justification is required before granting an exception.
                    </p>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          {/* ─── Context cards ─────────────────────────────────────── */}
          <section>
            <SectionHead icon={FileText} title="Registration context" />
            <ContextCards
              holding={holding}
              warrantyIntentLabel={warrantyIntentLabel}
            />
          </section>

          {/* ─── Claim history band ────────────────────────────────── */}
          <ClaimHistoryBand claims={claims} cover={currentCoverLabel} />

          {/* ─── Super Admin footer ────────────────────────────────── */}
          <div className="-mx-10 flex flex-wrap items-center justify-between gap-3 border-t-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-10 py-3">
            <span className="clara-label">Super Admin</span>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" disabled>
                Cancel terms
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled
                className="border-[rgba(153,27,27,0.22)] text-[var(--status-error)]"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* ─── Dark sticky action slab ─────────────────────────────── */}
        <div className="sticky bottom-5 z-10 mx-10 mb-5 mt-6 rounded-[14px] bg-[var(--ink)] px-7 py-5 text-[#f0ebdd] shadow-[0_18px_36px_-14px_rgba(28,28,26,0.45)]">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[13.5px] font-medium text-white">
                {allClear
                  ? `Ready to grant ${upgradeTierLabel}.`
                  : `${selectedCodes.size} exception${selectedCodes.size === 1 ? "" : "s"} selected · ${outstanding} rule${outstanding === 1 ? "" : "s"} blocking ${upgradeTierLabel}`}
              </p>
              <p className="mt-1 text-[11.5px] text-[#c4bea8]">
                {t(
                  "registrations.coverActiveDenyKeeps",
                  "{{cover}} warranty is already on. Denying this upgrade keeps the registration on {{cover}} - nothing is lost.",
                  { cover: currentCoverLabel },
                )}
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <Button
                variant="outline"
                disabled={!canApprove}
                className="h-10 border-[#4a4843] bg-transparent px-4 text-[11.5px] uppercase tracking-[0.09em] text-[#e0dbc8] hover:border-[#8a877f]"
              >
                {t("registrations.keepOnCover", "Keep on {{cover}}", {
                  cover: currentCoverLabel,
                })}
              </Button>
              {canApprove ? (
                <Button
                  onClick={activate}
                  disabled={
                    resolveMutation.isPending ||
                    (outstanding > 0 && selectedCodes.size === 0)
                  }
                  className="h-10 bg-[var(--green)] px-5 text-[11.5px] uppercase tracking-[0.09em] text-white hover:bg-[#2f5439]"
                >
                  {resolveMutation.isPending
                    ? "Granting…"
                    : allClear
                      ? `Grant ${upgradeTierLabel} →`
                      : `Grant ${upgradeTierLabel} with ${outstanding} override${outstanding === 1 ? "" : "s"} →`}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Bull() {
  return <span className="mx-1.5 text-[var(--ink-3)]">·</span>;
}

function SectionHead({
  icon: Icon,
  title,
  right,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-[var(--ink-3)]" />
        <span className="clara-label">{title}</span>
      </div>
      {right}
    </div>
  );
}

type TileTone = "success" | "warn" | "err" | "neutral" | "platinum";

function SignalTile({
  label,
  icon: Icon,
  value,
  tone = "neutral",
  meta,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  tone?: TileTone;
  meta?: string;
}) {
  const valueClass =
    tone === "success"
      ? "text-[var(--status-ok)]"
      : tone === "err"
        ? "text-[var(--status-error)]"
        : tone === "warn"
          ? "text-[var(--status-warn)]"
          : "text-[var(--ink)]";

  return (
    <div className="rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-6 py-5">
      <div className="mb-3 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-[var(--ink-3)]" />
        <p className="clara-label">{label}</p>
      </div>
      {tone === "platinum" ? (
        <p className="text-[16px] leading-tight">
          <span className="inline-flex items-center gap-1 rounded-full border-[0.5px] border-[#44413a] bg-gradient-to-br from-[#2a2925] to-[#44413a] px-3 py-1 text-[13px] font-medium text-[#eee6cc]">
            {value}
          </span>
        </p>
      ) : (
        <p className={cn("text-[18px] font-medium leading-tight", valueClass)}>
          {value}
        </p>
      )}
      {meta ? (
        <p className="mt-1.5 text-[11.5px] text-[var(--ink-3)]">{meta}</p>
      ) : null}
    </div>
  );
}

function IssueRow({
  issue,
  first,
  summary,
  detail,
  canEdit,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onResolve,
}: {
  issue: WorkflowIssue;
  first: boolean;
  summary: string;
  detail: string;
  canEdit: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (summary: string, detail: string) => void;
  onResolve: () => void;
}) {
  const tone: "warn" | "err" = /payment|unpaid|blocked/.test(
    `${issue.predicate_key} ${issue.issue_kind}`.toLowerCase(),
  )
    ? "err"
    : "warn";
  const IconWrap = tone === "err" ? XCircle : AlertTriangle;
  const wrapClass =
    tone === "err"
      ? "bg-[#efd8cf] text-[var(--status-error)]"
      : "bg-[#f5ecd3] text-[var(--status-warn)]";

  return (
    <RuleRowShell
      first={first}
      wrapClass={wrapClass}
      icon={<IconWrap className="h-4 w-4" strokeWidth={2.25} />}
      summary={summary}
      detail={detail}
      canEdit={canEdit}
      isEditing={isEditing}
      onStartEdit={onStartEdit}
      onCancelEdit={onCancelEdit}
      onSave={onSave}
      onResolve={onResolve}
      chip={
        <span
          className={cn(
            "mt-0.5 shrink-0 rounded-[4px] border-[0.5px] px-2 py-0.5 text-[10px] uppercase tracking-[0.1em]",
            tone === "err"
              ? "border-[rgba(153,27,27,0.22)] bg-[#efd8cf] text-[var(--status-error)]"
              : "border-[rgba(181,133,26,0.3)] bg-[#f5ecd3] text-[var(--status-warn)]",
          )}
        >
          Needs judgement
        </span>
      }
    />
  );
}

function CustomRuleRow({
  rule,
  first,
  canEdit,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onResolve,
}: {
  rule: { id: string; summary: string; detail: string };
  first: boolean;
  canEdit: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (summary: string, detail: string) => void;
  onDelete: () => void;
  onResolve: () => void;
}) {
  return (
    <RuleRowShell
      first={first}
      wrapClass="bg-[var(--bg-subtle)] text-[var(--ink-3)]"
      icon={<Sparkles className="h-4 w-4" strokeWidth={2.25} />}
      summary={rule.summary}
      detail={rule.detail}
      canEdit={canEdit}
      isEditing={isEditing}
      onStartEdit={onStartEdit}
      onCancelEdit={onCancelEdit}
      onSave={onSave}
      onDelete={onDelete}
      onResolve={onResolve}
      chip={
        <span className="mt-0.5 shrink-0 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Custom
        </span>
      }
    />
  );
}

function RuleRowShell({
  first,
  wrapClass,
  icon,
  summary,
  detail,
  canEdit,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onResolve,
  chip,
}: {
  first: boolean;
  wrapClass: string;
  icon: React.ReactNode;
  summary: string;
  detail: string;
  canEdit: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (summary: string, detail: string) => void;
  onDelete?: () => void;
  onResolve?: () => void;
  chip: React.ReactNode;
}) {
  return (
    <li
      className={cn(
        "flex items-start gap-5 px-7 py-5",
        !first && "border-t-[0.5px] border-[var(--rule)]",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          wrapClass,
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <RuleEditForm
            initialSummary={summary}
            initialDetail={detail}
            onSave={onSave}
            onCancel={onCancelEdit}
            onDelete={onDelete}
          />
        ) : (
          <>
            <p className="text-[14.5px] font-medium leading-snug text-[var(--ink)]">
              {summary}
            </p>
            {detail ? (
              <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--ink-2)]">
                {detail}
              </p>
            ) : null}
          </>
        )}
      </div>
      {!isEditing ? (
        <div className="flex shrink-0 items-start gap-2">
          {chip}
          {onResolve ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={onResolve}
              className="h-6 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] px-3 text-[10.5px] font-medium tracking-[0.02em] text-[var(--ink-2)] hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
            >
              Resolve
            </Button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              onClick={onStartEdit}
              aria-label="Edit rule"
              className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] transition-colors hover:border-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Pencil className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Extracted so the edit form's local draft state is reset naturally
 * via remount each time the row enters edit mode - no useEffect
 * chase needed.
 */
function RuleEditForm({
  initialSummary,
  initialDetail,
  onSave,
  onCancel,
  onDelete,
}: {
  initialSummary: string;
  initialDetail: string;
  onSave: (summary: string, detail: string) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [draftSummary, setDraftSummary] = useState(initialSummary);
  const [draftDetail, setDraftDetail] = useState(initialDetail);
  return (
    <div className="flex flex-col gap-2">
      <input
        value={draftSummary}
        onChange={(e) => setDraftSummary(e.target.value)}
        placeholder="Rule title"
        className="w-full rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-1.5 text-[14px] font-medium text-[var(--ink)] focus:border-[var(--ink-3)] focus:outline-none"
      />
      <textarea
        value={draftDetail}
        onChange={(e) => setDraftDetail(e.target.value)}
        placeholder="Explanation - what does this rule mean?"
        rows={2}
        className="w-full rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-1.5 text-[13px] leading-relaxed focus:border-[var(--ink-3)] focus:outline-none"
      />
      <div className="flex items-center gap-2">
        <Button
          size="xs"
          onClick={() => onSave(draftSummary.trim(), draftDetail.trim())}
        >
          Save
        </Button>
        <Button size="xs" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {onDelete ? (
          <Button
            size="xs"
            variant="outline"
            onClick={onDelete}
            className="ml-auto border-[rgba(153,27,27,0.22)] text-[var(--status-error)]"
          >
            Delete
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function AddRuleForm({
  first,
  onCancel,
  onSave,
}: {
  first: boolean;
  onCancel: () => void;
  onSave: (summary: string, detail: string) => void;
}) {
  const [summary, setSummary] = useState("");
  const [detail, setDetail] = useState("");
  return (
    <li
      className={cn(
        "flex items-start gap-5 bg-[var(--bg-subtle)] px-7 py-5",
        !first && "border-t-[0.5px] border-[var(--rule)]",
      )}
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--bg)] text-[var(--ink-3)]">
        <Plus className="h-4 w-4" strokeWidth={2.25} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <input
          autoFocus
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="New rule title - e.g. Property in coverage zone"
          className="w-full rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-1.5 text-[14px] font-medium text-[var(--ink)] focus:border-[var(--ink-3)] focus:outline-none"
        />
        <textarea
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="Explanation - when does this rule block the upgrade?"
          rows={2}
          className="w-full rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-1.5 text-[13px] leading-relaxed focus:border-[var(--ink-3)] focus:outline-none"
        />
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            disabled={!summary.trim()}
            onClick={() => onSave(summary.trim(), detail.trim())}
          >
            Add rule
          </Button>
          <Button size="xs" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </li>
  );
}

function ExceptionRow({
  label,
  coverageCount,
  seededNote,
  selected,
  canEdit,
  isEditing,
  onToggle,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
}: {
  code: string;
  label: string;
  coverageCount: number | null;
  seededNote: string;
  selected: boolean;
  canEdit: boolean;
  isEditing: boolean;
  onToggle: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (label: string) => void;
  onDelete?: () => void;
}) {
  return (
    <div
      className={cn(
        "flex gap-3 border-t-[0.5px] border-dashed border-[var(--rule)] py-3 first:border-t-0 first:pt-0",
        !canEdit && "opacity-60",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!canEdit || isEditing}
        aria-label={selected ? "Untick" : "Tick"}
        className={cn(
          "mt-0.5 flex h-[18px] w-[18px] shrink-0 cursor-pointer items-center justify-center rounded-[4px] border-[0.5px] transition-colors",
          selected
            ? "border-[var(--green)] bg-[var(--green)] text-white"
            : "border-[var(--rule-strong)] bg-[var(--bg)]",
        )}
      >
        {selected ? <Check className="h-3 w-3" strokeWidth={2.5} /> : null}
      </button>
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <ExcEditForm
            initialLabel={label}
            onSave={onSave}
            onCancel={onCancelEdit}
            onDelete={onDelete}
          />
        ) : (
          <>
            <p className="text-[13.5px] font-medium text-[var(--ink)]">
              {label}
              {coverageCount !== null ? (
                <span className="ml-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--green)]">
                  {` · Covers ${coverageCount} rule${coverageCount === 1 ? "" : "s"}`}
                </span>
              ) : null}
            </p>
            <p className="mt-0.5 text-[12px] leading-relaxed text-[var(--ink-3)]">
              {seededNote}
            </p>
          </>
        )}
      </div>
      {!isEditing && canEdit ? (
        <button
          type="button"
          onClick={onStartEdit}
          aria-label="Edit exception"
          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] transition-colors hover:border-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <Pencil className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
}

function ExcEditForm({
  initialLabel,
  onSave,
  onCancel,
  onDelete,
}: {
  initialLabel: string;
  onSave: (label: string) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [draftLabel, setDraftLabel] = useState(initialLabel);
  return (
    <div className="flex flex-col gap-2">
      <input
        value={draftLabel}
        onChange={(e) => setDraftLabel(e.target.value)}
        autoFocus
        className="w-full rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-1.5 text-[13.5px] font-medium text-[var(--ink)] focus:border-[var(--ink-3)] focus:outline-none"
      />
      <div className="flex items-center gap-2">
        <Button size="xs" onClick={() => onSave(draftLabel.trim())}>
          Save
        </Button>
        <Button size="xs" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {onDelete ? (
          <Button
            size="xs"
            variant="outline"
            onClick={onDelete}
            className="ml-auto border-[rgba(153,27,27,0.22)] text-[var(--status-error)]"
          >
            Delete
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function AddExceptionForm({
  onCancel,
  onSave,
}: {
  onCancel: () => void;
  onSave: (label: string) => void;
}) {
  const [label, setLabel] = useState("");
  return (
    <div className="flex gap-3 border-t-[0.5px] border-dashed border-[var(--rule)] py-3 first:border-t-0 first:pt-0">
      <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[var(--ink-3)]">
        <Plus className="h-3 w-3" strokeWidth={2.25} />
      </span>
      <div className="flex flex-1 flex-col gap-2">
        <input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="New exception - e.g. Platinum service-area override"
          className="w-full rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-1.5 text-[13.5px] font-medium text-[var(--ink)] focus:border-[var(--ink-3)] focus:outline-none"
        />
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            disabled={!label.trim()}
            onClick={() => onSave(label.trim())}
          >
            Add exception
          </Button>
          <Button size="xs" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function ContextCards({
  holding,
  warrantyIntentLabel,
}: {
  holding: HoldingShape | undefined;
  warrantyIntentLabel: string;
}) {
  const consumer = holding?.consumer_account;
  const consumerName =
    [consumer?.first_name, consumer?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || "-";
  const host = holding?.host;
  const addressLine = addressDisplay(host?.address) || host?.name || "-";
  const structureLabel =
    host?.host_template?.name ||
    (host?.kind === "PROPERTY"
      ? "Property"
      : host?.kind === "VESSEL"
        ? "Vessel"
        : host?.kind === "VEHICLE"
          ? "Vehicle"
          : "-");
  const contractor = holding?.partnership?.downstream_organisation_name ?? "-";

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <ContextCard
        icon={<UserCircle className="h-3.5 w-3.5" />}
        label="Homeowner"
      >
        <CtxRow k="Name" v={consumerName} />
        <CtxRow k="Email" v={consumer?.email ?? "-"} />
        <CtxRow
          k="Phone"
          v={formatPhoneDisplay(consumer?.phone_number) || "-"}
        />
      </ContextCard>

      <ContextCard icon={<MapPin className="h-3.5 w-3.5" />} label="Property">
        <CtxRow k="Address" v={addressLine} />
        <CtxRow k="Structure" v={structureLabel} />
      </ContextCard>

      <ContextCard
        icon={<Building2 className="h-3.5 w-3.5" />}
        label="Contractor"
      >
        <CtxRow k="Name" v={contractor} />
        <CtxRow k="Product" v={holding?.product?.cached_short_name ?? "-"} />
        <CtxRow k="Warranty" v={warrantyIntentLabel} />
      </ContextCard>
    </div>
  );
}

function ContextCard({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[10px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
      <div className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-4 py-3">
        <span className="text-[var(--ink-3)]">{icon}</span>
        <span className="clara-label">{label}</span>
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function CtxRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b-[0.5px] border-[var(--rule)] px-4 py-2.5 last:border-b-0">
      <span className="shrink-0 text-[11.5px] text-[var(--ink-3)]">{k}</span>
      <span className="min-w-0 truncate text-right text-[13px] text-[var(--ink)]">
        {v}
      </span>
    </div>
  );
}

function ClaimHistoryBand({
  claims,
  cover,
}: {
  claims: ClaimRow[];
  cover: string;
}) {
  const { t } = useTranslation();
  const openCount = claims.filter((c) => c.statusVariant === "open").length;
  return (
    <section>
      <SectionHead
        icon={Users}
        title="Claim history"
        right={
          <span className="text-[11px] text-[var(--ink-3)] tabular-nums">
            {claims.length === 0
              ? "No previous claims"
              : `${claims.length} previous${openCount > 0 ? ` · ${openCount} open` : ""}`}
          </span>
        }
      />
      <div className="overflow-hidden rounded-[10px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
        {claims.length === 0 ? (
          <p className="px-4 py-4 text-[12.5px] text-[var(--ink-3)] [font-family:'Cormorant_Garamond',Georgia,serif]">
            {t(
              "registrations.noClaimsCoverAll",
              "No previous claims on this property. {{cover}} warranty covers all claims.",
              { cover },
            )}
          </p>
        ) : (
          <ul>
            {claims.map((claim, idx) => (
              <li
                key={claim.uuid}
                className={cn(
                  "flex flex-wrap items-center gap-4 px-4 py-3",
                  idx > 0 && "border-t-[0.5px] border-[var(--rule)]",
                )}
              >
                <span className="shrink-0 text-[11.5px] tabular-nums text-[var(--ink-3)]">
                  {claim.raisedOn}
                </span>
                <span className="flex-1 min-w-0 truncate text-[12.5px] text-[var(--ink)]">
                  {claim.summary}
                </span>
                {claim.amount ? (
                  <span className="text-[11.5px] tabular-nums text-[var(--ink-3)]">
                    {claim.amount}
                  </span>
                ) : null}
                <ClaimChip
                  variant={claim.statusVariant}
                  label={claim.statusLabel}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ClaimChip({
  variant,
  label,
}: {
  variant: ClaimRow["statusVariant"];
  label: string;
}) {
  const styles: Record<ClaimRow["statusVariant"], string> = {
    open: "bg-[var(--status-info-bg)] text-[var(--status-info)] border-[var(--rule-strong)]",
    paid: "bg-[var(--status-ok-bg)] text-[var(--status-ok)] border-[var(--status-ok-border)]",
    closed:
      "bg-[var(--bg-subtle)] text-[var(--ink-3)] border-[var(--rule-strong)]",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-[4px] border-[0.5px] px-2 py-0.5 text-[10px] uppercase tracking-[0.1em]",
        styles[variant],
      )}
    >
      {label}
    </span>
  );
}
